import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ensureLanguageLoaded, notifyLocaleChanged, t } from '../../i18n/uiText';
import { sortLanguagesByDisplayOrder } from '../../utils/sortLanguages';
import { detectUiLanguage, FALLBACK_UI_LANGUAGE, rememberLanguageChoice } from '../../utils/localeBootstrap';

const API_BASE = import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';

export default function LanguagePromptModal({ onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('bibleLanguage') || detectUiLanguage();
    }
    return FALLBACK_UI_LANGUAGE;
  });

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    async function loadLanguages() {
      try {
        const response = await fetch(`${API_BASE}/languages`);
        if (!response.ok) throw new Error('Failed to load languages');
        const data = await response.json();
        setLanguages(sortLanguagesByDisplayOrder(data.languages || []));
      } catch (error) {
        console.error('Error loading languages:', error);
      } finally {
        setLoading(false);
      }
    }
    loadLanguages();
  }, []);

  const handleSelectLanguage = async (lang) => {
    await ensureLanguageLoaded(lang.code);
    setSelectedLanguage(lang.code);
    if (typeof window !== 'undefined') {
      rememberLanguageChoice(lang.code);
      notifyLocaleChanged();
      if (lang.defaultTranslation) {
        localStorage.setItem('bibleTranslation', lang.defaultTranslation);
      }
    }

    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
    if (token) {
      try {
        await fetch(`${API_BASE}/me/preferred-language`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ language: lang.code }),
        });
      } catch (e) {
        console.error('Failed to save preferred language', e);
      }
    }

    const targetTranslation =
      lang.defaultTranslation ||
      (Array.isArray(lang.translations) && lang.translations[0]?.id) ||
      'kjv';

    if (targetTranslation) {
      const parts = location.pathname.split('/').filter(Boolean);
      if (parts.length > 0) {
        parts[0] = targetTranslation;
        navigate('/' + parts.join('/'), { replace: true });
      } else {
        navigate('/' + targetTranslation, { replace: true });
      }
    }

    onClose?.();
  };

  if (loading) {
    return (
      <div className="history-modal" onClick={onClose}>
        <div className="history-content" onClick={(e) => e.stopPropagation()}>
          <div className="bible-modal-sheet-handle" aria-hidden="true" />
          <div className="chapter-selector-content-header">
            <h2 className="text-base font-semibold text-slate-700">{t('languagePromptTitle')}</h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex-shrink-0 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors text-lg font-light"
              aria-label={t('close')}
            >
              ×
            </button>
          </div>
          <div className="chapter-selector-scroll">
            <div className="text-center py-8">
              <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-500 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-600 font-semibold">{t('loading')}...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="history-modal" onClick={onClose}>
      <div className="history-content" onClick={(e) => e.stopPropagation()}>
        <div className="bible-modal-sheet-handle" aria-hidden="true" />
          <div className="chapter-selector-content-header">
          <h2 className="text-base font-semibold text-slate-700">{t('languagePromptTitle')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex-shrink-0 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors text-lg font-light"
            aria-label={t('close')}
          >
            ×
          </button>
        </div>

        <div className="chapter-selector-scroll">
          <section className="mb-4">
            <ul className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50 shadow-sm">
              {languages.map((lang) => (
                <li key={lang.code}>
                  <button
                    type="button"
                    onClick={() => handleSelectLanguage(lang)}
                    className={`w-full flex items-center justify-between px-4 py-3.5 text-left transition-colors border-b border-slate-200 last:border-b-0 ${
                      selectedLanguage === lang.code
                        ? 'bg-slate-100 text-slate-700'
                        : 'bg-transparent text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <span className="font-medium">{lang.name}</span>
                    {selectedLanguage === lang.code && (
                      <svg className="w-5 h-5 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                  {selectedLanguage === lang.code && lang.translations?.length > 1 && (
                    <div className="px-4 pb-3 pt-1 bg-slate-100 border-t border-slate-200">
                      <p className="text-xs text-slate-500 leading-snug">
                        {t('settingsTranslationsLabel')}: {lang.translations.map((tr) => tr.name).join(', ')}
                      </p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
